
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { RoleWithPermissions, FeaturePermission } from '@/types/userRole';
import PermissionsList from './PermissionsList';

interface RoleEditDialogProps {
  open: boolean;
  title: string;
  description: string;
  role?: RoleWithPermissions;
  availablePermissions: FeaturePermission[];
  onClose: () => void;
  onSave: (role: Omit<RoleWithPermissions, 'id' | 'userCount'>) => void;
  confirmText: string;
}

const RoleEditDialog: React.FC<RoleEditDialogProps> = ({
  open,
  title,
  description,
  role,
  availablePermissions,
  onClose,
  onSave,
  confirmText
}) => {
  const [name, setName] = useState(role?.name || '');
  const [roleDescription, setRoleDescription] = useState(role?.description || '');
  const [selectedPermissions, setSelectedPermissions] = useState<string[]>(role?.permissions || []);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState('details');

  // Group permissions by category for the UI
  const permissionsByCategory: Record<string, FeaturePermission[]> = {};
  availablePermissions.forEach(permission => {
    if (!permissionsByCategory[permission.category]) {
      permissionsByCategory[permission.category] = [];
    }
    permissionsByCategory[permission.category].push(permission);
  });

  const categories = Object.keys(permissionsByCategory).sort();

  useEffect(() => {
    if (role) {
      setName(role.name);
      setRoleDescription(role.description);
      setSelectedPermissions(role.permissions);
    }
  }, [role]);

  const handleSubmit = async () => {
    if (!name.trim()) return;
    
    setIsSubmitting(true);
    try {
      await onSave({
        name,
        description: roleDescription,
        permissions: selectedPermissions,
        isDefault: role?.isDefault || false,
        isSystem: role?.isSystem || false
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const togglePermission = (permissionId: string) => {
    setSelectedPermissions(prev => 
      prev.includes(permissionId)
        ? prev.filter(id => id !== permissionId)
        : [...prev, permissionId]
    );
  };

  const toggleAllInCategory = (category: string, enabled: boolean) => {
    const categoryPermissionIds = permissionsByCategory[category].map(p => p.id);
    
    if (enabled) {
      // Add all permissions from this category that aren't already selected
      setSelectedPermissions(prev => {
        const newPermissions = [...prev];
        categoryPermissionIds.forEach(id => {
          if (!newPermissions.includes(id)) {
            newPermissions.push(id);
          }
        });
        return newPermissions;
      });
    } else {
      // Remove all permissions from this category
      setSelectedPermissions(prev => 
        prev.filter(id => !categoryPermissionIds.includes(id))
      );
    }
  };

  return (
    <Dialog open={open} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-[600px] max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        
        <Tabs value={activeTab} onValueChange={setActiveTab} className="mt-4">
          <TabsList className="grid grid-cols-2">
            <TabsTrigger value="details">Role Details</TabsTrigger>
            <TabsTrigger value="permissions">Permissions</TabsTrigger>
          </TabsList>
          
          <TabsContent value="details" className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label htmlFor="name">Role Name</Label>
              <Input
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g., Manager, Editor, etc."
                disabled={role?.isSystem}
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={roleDescription}
                onChange={(e) => setRoleDescription(e.target.value)}
                placeholder="Describe the purpose and capabilities of this role"
                rows={3}
              />
            </div>
          </TabsContent>
          
          <TabsContent value="permissions" className="space-y-4 mt-4">
            <div className="bg-muted/50 p-3 rounded-md mb-4">
              <p className="text-sm">
                Select the permissions this role should have. Users with this role will only be able to access features they have permission for.
              </p>
            </div>
            
            {categories.map(category => (
              <PermissionsList 
                key={category}
                category={category}
                permissions={permissionsByCategory[category]}
                selectedPermissions={selectedPermissions}
                onTogglePermission={togglePermission}
                onToggleAll={toggleAllInCategory}
              />
            ))}
          </TabsContent>
        </Tabs>

        <DialogFooter className="mt-6">
          <Button 
            variant="outline" 
            onClick={onClose}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleSubmit}
            disabled={!name.trim() || isSubmitting}
          >
            {isSubmitting ? (
              <>
                <span className="animate-spin mr-2">⟳</span>
                Saving...
              </>
            ) : confirmText}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default RoleEditDialog;
